import type { AnomalyDetail, WeldAnalyticsBundle } from '../types/weld';

/** Worst-first order for anomaly review priorities. */
export const REVIEW_PRIORITY_RANK: Record<string, number> = {
  Critical: 0,
  High: 1,
  Medium: 2,
  Watch: 3,
  Normal: 4,
  Unknown: 5,
};

/**
 * Worst review_priority among anomalies for a weld+job (Critical > High > …).
 */
export function worstReviewPriorityForWeld(
  anomalies: AnomalyDetail[],
  weldNumber: string,
  jobNumber: string | undefined,
): string {
  const matches = anomalies.filter(
    (a) =>
      String(a.weld_number) === String(weldNumber) &&
      (jobNumber == null || jobNumber === '' || String(a.job_number) === String(jobNumber)),
  );
  if (!matches.length) return '—';

  let best = matches[0].review_priority || 'Unknown';
  let bestRank = REVIEW_PRIORITY_RANK[best] ?? REVIEW_PRIORITY_RANK.Unknown;
  for (const a of matches) {
    const p = a.review_priority || 'Unknown';
    const r = REVIEW_PRIORITY_RANK[p] ?? REVIEW_PRIORITY_RANK.Unknown;
    if (r < bestRank) {
      best = p;
      bestRank = r;
    }
  }
  return best;
}

export function buildWeldPriorityMap(bundle: WeldAnalyticsBundle): Map<string, string> {
  const map = new Map<string, string>();
  const key = (job: string, weld: string) => `${job}::${weld}`;
  for (const w of bundle.welds) {
    map.set(
      key(String(w.job_number ?? ''), String(w.weld_number)),
      worstReviewPriorityForWeld(bundle.anomalies, w.weld_number, w.job_number),
    );
  }
  return map;
}

/** Worst review priority from a drill-down row's bundled anomalies. */
export function reviewPriorityFromAnomalies(
  anomalies: { review_priority?: string }[],
): string {
  if (!anomalies.length) return '—';
  let best = anomalies[0].review_priority || 'Unknown';
  let bestRank = REVIEW_PRIORITY_RANK[best] ?? REVIEW_PRIORITY_RANK.Unknown;
  for (const a of anomalies) {
    const p = a.review_priority || 'Unknown';
    const r = REVIEW_PRIORITY_RANK[p] ?? REVIEW_PRIORITY_RANK.Unknown;
    if (r < bestRank) {
      best = p;
      bestRank = r;
    }
  }
  return best;
}
